import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import wetvKernel from '@tencent/wetv-kernel';
import { setFocus } from '@noriginmedia/norigin-spatial-navigation';
import { dialogFocusLock } from '@/utils/dialogFocusLock';
import { logout } from '@/services/authApi';
import { getCookieAmend, clearAllCookie, delLocalStorage } from '@/utils/apiUtils';
import { useVipInfo } from '@/hooks/useVipInfo';
import { SourcePageEnum } from '@/types/vipOpenPage';
import { sourceValEnum } from '@/pages/VipOpenPage/constants/vipOpenPage';
import { PLAYER_STORAGE_KEYS, usePlayerStore } from '@/stores/playerStore';
import { useUserInfoStore } from '@/stores/userInfoStore';
import { emitLogoutEvent } from '@/utils/authEventManager';
import { tokenRefreshManager } from '@/utils/tokenRefreshManager';

const { user } = wetvKernel;
const { HomePage } = SourcePageEnum;

/**
 * 用户认证相关的 Hook
 */
export function useUserAuth(pathname: string) {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userAvatar, setUserAvatar] = useState('');
  const [userNick, setUserNick] = useState('');
  const { vipInfo } = useVipInfo();

  // 订阅 userInfoStore，实时响应用户状态变化
  const userInfo = useUserInfoStore((state) => state.userInfo);

  // 检查用户登录状态 - 同时监听 pathname 和 userInfo 变化
  useEffect(() => {
    const checkLoginStatus = () => {
      const loginStatus = user?.isLogin ?? false;
      const avatar = user?.avatar ?? '';
      const nick = user?.nick ?? '';
      setIsLoggedIn(loginStatus);
      setUserAvatar(avatar);
      setUserNick(nick);
    };

    checkLoginStatus();
  }, [pathname, userInfo]); // 添加 userInfo 依赖，登录成功后立即更新

  /**
   * 处理登出
   */
  const handleLogout = useCallback(async () => {
    try {
      const body = {
        guid: getCookieAmend('guid'),
        vplatform: getCookieAmend('vplatform'),
      };

      const res = await logout(body);

      if (res && !res.code && !res.errCode && !res.err_code) {
        // 清除缓存标记
        sessionStorage.setItem('isCache', '1');
        localStorage.removeItem('nextFreshDate');
        delLocalStorage('Complete_Birthay');

        // 清除票据刷新定时器（对标 tv-linux 的刷票逻辑）
        try {
          tokenRefreshManager.clearRefreshTimer();
          console.log('[useUserAuth] 已清除票据刷新定时器');
        } catch (error) {
          console.error('[useUserAuth] 清除票据刷新定时器失败:', error);
        }

        // 如果当前清晰度是1080P（fhd），清除清晰度设置并降级播放器到720P
        const savedQuality = localStorage.getItem(PLAYER_STORAGE_KEYS.QUALITY);
        const currentQuality = usePlayerStore.getState().quality;
        if (savedQuality && savedQuality.toLowerCase() === 'fhd') {
          localStorage.removeItem(PLAYER_STORAGE_KEYS.QUALITY);
        }
        // 如果播放器当前是1080P，立即降级到720P（退出会员后无权继续使用1080P）
        if (currentQuality && currentQuality.toLowerCase() === 'fhd') {
          usePlayerStore
            .getState()
            .setQuality({ quality: 'shd', showToast: false })
            .catch((error) => {
              console.warn('[useUserAuth] 退出登录时降级清晰度失败:', error);
            });
        }

        // 清除所有 cookie（除了白名单）
        clearAllCookie();

        // 关键修复：立即清除 wetvKernel.user 的内部缓存
        // 确保头像和昵称立即清空，不需要等待页面刷新
        try {
          if (wetvKernel?.user?.setUserInfo) {
            wetvKernel.user.setUserInfo({
              avatar: '',
              nick: '',
              birth: '',
            });
            console.log('[useUserAuth] 已清除 wetvKernel.user 内部缓存');
          } else {
            console.warn('[useUserAuth] wetvKernel.user 不可用，跳过内部缓存清除');
          }
        } catch (error) {
          console.error('[useUserAuth] 清除 wetvKernel.user 内部缓存失败:', error);
        }

        // 更新 Aegis 上报的用户 ID
        const { ageisReportor } = wetvKernel;
        if (ageisReportor?.updateUin) {
          ageisReportor.updateUin({
            vuserid: '',
            vusession: getCookieAmend('vusession'),
          });
        }

        // 刷新用户状态（无需刷新页面）
        const refreshUserInfo = useUserInfoStore.getState().refresh;

        // 立即刷新，然后延迟刷新确保数据同步
        refreshUserInfo();
        setTimeout(() => {
          refreshUserInfo();
        }, 150);
        setTimeout(() => {
          refreshUserInfo();
        }, 500);

        // 触发用户信息变更事件（让外部监听器可以响应登录态变更）
        try {
          emitLogoutEvent();
          console.log('[useUserAuth] 已触发 userInfoChange 事件（退出登录）');
        } catch (error) {
          console.error('[useUserAuth] 触发退出登录事件失败:', error);
        }

        // 如果当前在需要登录的页面（如历史记录、会员页），跳转到首页
        const protectedPaths = ['/history', '/vip/', '/settings'];
        const shouldRedirect = protectedPaths.some((path) => pathname.startsWith(path));

        if (shouldRedirect) {
          navigate('/');
        }

        // 等待焦点锁解除
        const waitForUnlock = () => {
          if (!dialogFocusLock.isLocked()) {
            console.log('[useUserAuth] 焦点锁已解除，立即设置焦点');
            // 直接设置焦点到第一个频道按钮（channelId=1001, channelType=0）
            setFocus('nav-channel-1001-0');
          } else {
            console.log('[useUserAuth] 焦点锁未解除，50ms 后重试');
            setTimeout(waitForUnlock, 50);
          }
        };

        // 立即开始等待
        waitForUnlock();
      }
    } catch {
      // 登出异常，继续执行
    }
  }, [navigate, pathname]);

  /**
   * 处理个人中心菜单选择
   */
  const handleProfileMenuSelect = useCallback(
    (menuKey: string) => {
      switch (menuKey) {
        case 'login':
          navigate('/account');
          break;
        case 'logout':
          void handleLogout();
          break;
        case 'settings':
          navigate('/settings');
          break;
        case 'history':
          navigate('/history');
          break;
        case 'join-vip':
          navigate(`/vip/open/${sourceValEnum[HomePage]}`);
          break;
        case 'about':
          navigate('/about');
          break;
        case 'feedback':
          navigate('/help-feedback');
          break;
      }
    },
    [navigate, handleLogout]
  );

  /**
   * 处理 Join VIP 点击
   */
  const handleJoinVip = useCallback(() => {
    navigate(`/vip/open/${sourceValEnum[HomePage]}`);
  }, [navigate]);

  return {
    isLoggedIn,
    userAvatar,
    userNick,
    vipInfo,
    handleProfileMenuSelect,
    handleJoinVip,
  };
}
